import {React, stringify, _} from '@api'
import {
  ProductLikeDefinition, IMAGE_OPERATION, REORDER_IMAGES_OPERATION, DELETE_CATALOGITEM, CREATE_TAG,
  DELETE_TAG, UPDATE_TAG, CustomAttributeType
} from '@api/graphql'
import {GroupByGroupStore, UNASSIGNED, wrapScalarOrArrayAsOneDArray} from '@modules'
import {IDragDropElement, IUpload, OFFLINE_ID, ui, ICatalogFieldConfig, VIPDropEventArgs, DetailsViewFieldConfig, StoresInstance, IHasFile} from '@stores'
import {IMutationObj} from '@stores/elasticsearch/mutations'
import {action, toJS, observable} from 'mobx'
import {tryParseJson} from '@utils'
import {IUpsertInput, IVipTag, IDeleteVipTag, IUpdateVipTag} from '@stores/elasticsearch'
import {CatalogInstanceChildStore} from './CatalogInstanceChildStore'
import {IAdditionalData, IImageOperationInput} from './CatalogDialogsStore'

export const DEBUG_CATALOG_MUTATIONS = false

export class CatalogMutationsStore extends CatalogInstanceChildStore {
  @action deleteCatalogItemsById = async (ids: string[]) => {
    const {catalog: {offlineItemsDb, index}, sidebar: {right}} = this,
      grouped = _.groupBy(ids, id => _.startsWith(id, OFFLINE_ID) ? 'offlineIds' : 'esIds'),
      {offlineIds, esIds} = grouped
    !_.isEmpty(offlineIds) && await offlineItemsDb.deleteItems(offlineIds)

    if (!_.isEmpty(esIds)) {
      const variables: any = {input: {index, data: esIds}}
      const {data: {deleteCatalogItems: response}} = await this.apollo.client.mutate({
        mutation: DELETE_CATALOGITEM,
        variables: variables
      })
      DEBUG_CATALOG_MUTATIONS && console.log({response})
    }
    if (right.open && _.some(ids, (cId) => cId === (right?.details?.data?._id || right?.details?.data?.id))) {
      DEBUG_CATALOG_MUTATIONS && console.log(`Closing right sidebar`)
      right?.details?.clearRightSidebarDetails()
    }
  }

  @action catalogImageOperation = async (input: IImageOperationInput) => {
    const {apollo, catalog} = this

    await apollo.client.mutate({
      mutation: IMAGE_OPERATION,
      variables: {
        input
      }
    })
    setTimeout(() => {
      catalog.refetch()
    }, 50)
  }
  
  @action catalogImageReorderOperation = async (input: IImageOperationInput) => {
    const {apollo, catalog} = this

    await apollo.client.mutate({
      mutation: REORDER_IMAGES_OPERATION,
      variables: {
        input
      }
    })

    setTimeout(() => {
      catalog.refetch()
    }, 50)
  }

  @action createCatalogTag = async (input: IUpsertInput) => {
    const inputValue: IVipTag = input.value,
      {apollo, catalog} = this
    await apollo.client.mutate({
      mutation: CREATE_TAG,
      variables: {
        input: inputValue
      }
    })
    setTimeout(() => catalog.refetch(), 50)
  }

  @observable loadingCatalogTag = false
  @action deleteCatalogTag = async (input: IDeleteVipTag | IAdditionalData) => {
    const {catalog: {refetch, filter: {refetchFilterByFields, state: {groups}, toggleFilter}}, apollo} = this,
      {tagPath, name} = input as IAdditionalData

    await apollo.client.mutate({
      mutation: DELETE_TAG,
      variables: {
        input
      }
    })
    if (groups[tagPath].selected.has(name)) {
      this.loadingCatalogTag = true
    }
    await this.deleteTagInOfflineItem(input)
    setTimeout(async () => {
      await refetch()
      await refetchFilterByFields([tagPath])
      if (groups[tagPath].selected.has(name)) {
        toggleFilter({key: tagPath, value: name})
        setTimeout(() => this.loadingCatalogTag = false, 500)
      }
    }, 50)
  }

  @action updateCatalogTag = async (input: IUpdateVipTag | IAdditionalData) => {
    const {apollo, stores} = this
    await apollo.client.mutate({
      mutation: UPDATE_TAG,
      variables: {
        input
      }
    })
    const {catalog: {refetch, componentStores: {itemsList}, groupBy: {isGroupBy}, filter: {toggleFilter, groupFor, refetchFilterByFields}}} = this,
      oldValue = itemsList?.activeEditItem?.title,
      key = itemsList?.library?.assignTo,
      hasThisTagInFilters = groupFor(key)?.selected?.has(oldValue),
      {tagPath} = input as IAdditionalData
    !isGroupBy && hasThisTagInFilters && (this.loadingCatalogTag = true)
    setTimeout(async () => {
      const newValue = itemsList?.activeEditItem?.name
      await refetch()
      await refetchFilterByFields([tagPath])
      await this.updateTagInOfflineItem(oldValue, newValue)

      setTimeout(() => {
        hasThisTagInFilters && toggleFilter({key, value: oldValue})
        hasThisTagInFilters && toggleFilter({key, value: newValue, item: groupFor(key)?.groupedByValue[newValue]})
        hasThisTagInFilters && setTimeout(() => this.loadingCatalogTag = false, 1000)
      }, 200)
    }, 50)
  }

  @action deleteTagInOfflineItem = async (tagData: IDeleteVipTag | IAdditionalData) => {
    const {catalog: {componentStores: {itemsList}, offlineItemsByIndex, index, upsertOfflineItem, catalogConfig: {tagType}}} = this,
      tagPath = itemsList?.library?.assignTo ?? `attributes.${tagType}s`
    await _.values(offlineItemsByIndex[index]).forEach(async offlineItem => {
      const item = {...offlineItem}
      let tagAttr = _.get(item, tagPath)
      _.remove(_.isArray(tagAttr) ? tagAttr : [], (tag: any) => tag?.name === tagData['name'] || tag?.id === tagData['id'])
      await upsertOfflineItem({index, value: item})
    })
  }

  @action updateTagInOfflineItem = async (oldTagName: string, newTagName: string) => {
    const {catalog: {componentStores: {itemsList}, offlineItemsByIndex, index, upsertOfflineItem, catalogConfig: {tagType}}} = this,
      tagPath = itemsList?.library?.assignTo ?? `attributes.${tagType}s`
    await _.values(offlineItemsByIndex[index]).forEach(async offlineItem => {
      const item = {...offlineItem}
      let tagAttr = _.get(item, tagPath)
      !_.isArray(tagAttr) && (tagAttr = [])
      const tagToUpdate = _.find(_.isArray(tagAttr) ? tagAttr : [], (tag: any) => tag?.name === oldTagName)
      tagToUpdate && (tagToUpdate.name = newTagName)
      await upsertOfflineItem({index, value: item})
    })
  }

  @action deleteItem = async (id: string) => {
    const {catalog: {selection, index, offlineItemsDb}, apollo} = this

    selection && selection.selectedIds.delete(id)

    if (id?.startsWith(OFFLINE_ID)) {
      await offlineItemsDb.deleteItem(id)
    } else {
      const variables: any = {input: {index, data: [id]}}
      const {data: {deleteCatalogItems: response}} = await apollo.client.mutate({
        mutation: DELETE_CATALOGITEM,
        variables: variables
      })
      return response
      // return await es.delete(id, catalog.index)
    }

    //return ProductService.removeProduct({where: {id}}, {updateQueryVariables})
  }

  @action deactivateItems = async (items: ProductLikeDefinition[], isCustomAttribute, productsData, isUnassigned, level) => {
    // let where = {}
    // if (isCustomAttribute) {
    //   let id_in = []
    //   if (!isUnassigned) {
    //     id_in = _.map(items[0].colorway, (product: any) => product.id)
    //   } else if (items[0].unassignedProducts) {
    //     id_in = _.map(items[0].unassignedProducts, (product) => product.id)
    //   } else if (items[0].colorway) {
    //     id_in = _.map(items[0].colorway, (product: any) => product.id)
    //   } else {
    //     id_in = _.map(productsData, (product: any) => product.id)
    //   }
    //   where = {
    //     product: {
    //       id_in
    //     }
    //   }
    // } else {
    //   if (items && items[0].unassignedProducts && level === 0) {
    //     where = {
    //       product: {
    //         id_in: _.map(items[0].unassignedProducts, (product) => product.id)
    //       }
    //     }
    //   } else {
    //     where =  {
    //       id_in:  _.map(productsData, product => product.colorway.id)
    //     }
    //   }
    // }
    // return await gqlApi.colorway.updateActiveFlagForManyColorways({
    //   variables: {
    //     where,
    //     action: 'DEACTIVATE'
    //   },
    // }, null)
  }

  @action unassign = async (group, path, isCustomAttribute, productsData, level) => {
    const {catalog: {groupBy: {hierarchy, groupingByName}}, site: {seasonal: {season, category1, category2}}} = this

    const firstLevelAttr = (path && path.length > 0) ? toJS(groupingByName.get(hierarchy[0]).nodes.get(path[0])).definition : toJS(groupingByName.get(hierarchy[0]).nodes.get(group)).definition
    const secondLevelAttr = (path && path.length > 0) ? toJS(groupingByName.get(hierarchy[1]).nodes.get(groupingByName.get(hierarchy[1]).pathLookup(group, path))).definition : null
    //
    // let where: any = {
    //   id_in: _.map(productsData, product => product.id)
    // }
    // let input = {}
    // if (secondLevelAttr ? hierarchy[1].startsWith('ca:') : hierarchy[0].startsWith('ca:')) {
    //   let customAttribute:any = (path && path.length > 0) ? secondLevelAttr : firstLevelAttr
    //   let caValues = customAttribute[colorWayConfig.customAttributeType[customAttribute.customAttributeDefinition.type]]
    //   caValues = _.isArray(caValues) ? _.map(caValues, value => value.id) : _.isObject(caValues) ? caValues.id : caValues
    //   input = {
    //     attributes: [{
    //       definitionName:                                                                       customAttribute.name,
    //       [colorWayConfig.customAttributeType[customAttribute.customAttributeDefinition.type]]: caValues
    //     }]
    //   }
    //   await gqlApi.products.unassignAttributesFromProducts({
    //     variables: {
    //       where,
    //       input
    //     }
    //   })
    //
    // } else {
    //   if ('materials' === hierarchy[0]) {
    //     !firstLevelAttr.id.includes("unassigned") && !secondLevelAttr && (input['material'] = firstLevelAttr.id)
    //   }
    //   if ('colors' === hierarchy[0]) {
    //     !firstLevelAttr.id.includes("unassigned") && !secondLevelAttr && (input['color'] = firstLevelAttr.id)
    //   }
    //   if ('styles' === hierarchy[0]) {
    //     !firstLevelAttr.id.includes("unassigned") && !secondLevelAttr && (input['sketch'] = firstLevelAttr.id)
    //   }
    //
    //   if ('materials' === hierarchy[1]) {
    //     secondLevelAttr && !secondLevelAttr.id.includes("unassigned") && (input['material'] = secondLevelAttr.id)
    //   }
    //   if ('colors' === hierarchy[1]) {
    //     secondLevelAttr && !secondLevelAttr.id.includes("unassigned") && (input['color'] = secondLevelAttr.id)
    //   }
    //   if ('styles' === hierarchy[1]) {
    //     secondLevelAttr && !secondLevelAttr.id.includes("unassigned") && (input['sketch'] = secondLevelAttr.id)
    //   }
    //
    //   await gqlApi.products.unassignAttributesFromProducts({
    //     variables: {
    //       where,
    //       input
    //     }
    //   })
    // }
    //
    // await queryManager.refetchAll()
  }

  @action deleteItems = async (products: any, isCustomAttribute, productsData, isUnassigned, level) => {

  }

  // TODO - these are all very ineffiencient as we lookup the full destination object when we only need the exId

  @action assignCatalogItemToLibraryItem = async (e: VIPDropEventArgs) => {
    const {drag, drop, context} = e,
      {catalog, es} = this,
      field = catalog._fieldByName[drag.dragType],
      {where} = drag
    let dropData: any = drag.id
    DEBUG_CATALOG_MUTATIONS && console.log('assignCatalogItemToLibraryItem', drag, drop)
    const {assignTo} = catalog.dragLibraryByIndex[drop.dragType],
      data = await es.client.get({id: drag.id, index: drag.dragType, _source: ['exId', 'id']})
    // return await assignAttributeToCatalogItem({assignTo, exId: drop.id, updatedValue: data})
    return await this.assignAttributeToCatalogItemNew({assignTo: field as Partial<DetailsViewFieldConfig>, where, updatedValue: dropData, query: `{"terms": {"exId": ${drop.id} }}`})
  }
  // Enumerable : Tag, secondary attribute

  @action dropOnGroupHeader = async (drag: IDragDropElement, drop: IDragDropElement, context) => {
    try {
      if (ui.settings.usingBulkAssignAPI || true) {
        this._assignDropToGroupHeader_bulk(drag, drop, context)
      } else {
        this._assignDropToGroupHeader_old(drag, drop, context)
      }
    } finally {
    }
  }

  @action private _assignDropToGroupHeader_old = async (drag: IDragDropElement, drop: IDragDropElement, context) => {
    const {store: groupStore} = drop.targetExtra,
      {es, catalog} = this,
      field = catalog._fieldByName[drag.dragType]
    //console.log(group)
    if (!groupStore) {
      DEBUG_CATALOG_MUTATIONS && console.error(`Missing group store node for`, drop)
      return null
    }

    const {childGroupStore: {productQueryVariables}, offlineItems, titleCount: size} = groupStore,
      isSecondariesDrop = drag.dragType.startsWith('secondaries'),
      _source = isSecondariesDrop ? ['_id', 'secondaries.*'] : ['_id', field.field]
    //console.log('productQueryVariables', productQueryVariables)

    const {items} = es.unwrapHitsResult(await es.search({
      index: catalog.index,
      meta: `Get field data for groupBy aggregate sidebar - group '${groupStore.groupTitleLabel}'`,
      body: {
        size,
        ...productQueryVariables,
        _source
      }
    }))
    await Promise.all([...items, ...(offlineItems || [])].map((item: any) => {
      const {assignAttributeToOfflineCatalogItem, assignAttributeToCatalogItem} = this
      if (item._id.includes(OFFLINE_ID)) {
        const val = isSecondariesDrop ? item.secondaries.map(s => {
          // TODO - Temporary fix, not recommended to use client specific attribute conditions ( which is not being enforced with new API)
          if (s.attributes.VIP_ParentName === drag.section) {
            if (_.has(s, 'attributes.asicsPreAdoption')) {s.attributes.asicsPreAdoption = drag['id']}
            drag['target'] && _.set(s, drag['target'].split('.').slice(1), drag['id'])
          }
          return s
        }) : drag['id']
        return assignAttributeToOfflineCatalogItem({assignTo: context, updatedValue: val, offlineId: item._id})
      } else {
        const enumListType = field.def?.type === 'EnumList',
          isUnassign = drag.id === UNASSIGNED,
          updatedValue = enumListType ? isUnassign ? [] : _.uniq([...(_.get(item, field.field) ?? []), drag.id]) : isSecondariesDrop ? item.secondaries.map(s => {
            if (s.attributes.VIP_ParentName === drag.section) {
              if (_.has(s, 'attributes.asicsPreAdoption')) {s.attributes.asicsPreAdoption = drag['id']}
              drag['target'] && _.set(s, drag['target'].split('.').slice(1), drag['id'])
            }
            return s
          }) : drag['id']

        return assignAttributeToCatalogItem({assignTo: context.field, where: drag.where, exId: item._id, updatedValue})
      }
    }))
  }

  @action private _assignDropToGroupHeader_bulk = async (drag: IDragDropElement, drop: IDragDropElement, context) => {
    const {assignAttributeToOfflineCatalogItem, assignAttributeToCatalogItemNew, catalog, es} = this,
      {store: groupStore} = drop.targetExtra

    //console.log(group)
    if (!groupStore) {
      DEBUG_CATALOG_MUTATIONS && console.error(`Missing group store node for`, drop)
      return null
    }
    try {
      const {childGroupStore: {productQueryVariables}, offlineItems, titleCount: size} = groupStore
      const field = this.catalog.fieldByName(context.field)
      const libraryItemId = drag.id,
        {where} = drag

      DEBUG_CATALOG_MUTATIONS && console.log(`drop on group header`, {drag}, {drop}, toJS(field))
      await Promise.all([...(offlineItems || [])].map((item: any) => {
        //TODO: Pending Item mutation to be handled with query
        assignAttributeToOfflineCatalogItem({assignTo: context, updatedValue: drag?.dragType === es.indexes.tag ? drag : drag['id'], offlineId: item._id})
      }))
      // if (field.section && _.find(sections,(section)=>{return field.section === section.id })) {//&& section.type ==='arrXref'
      //   // In this case the mutation we are doing has parent which has a nested-data-type (Example: mutation for one of the secondary attribute)
      // }

      let dropData: any = drag?.id ?? drag['id']
      switch (field?.def?.type) {
        case 'Ref':
        case 'RefList':
          dropData = dropData === UNASSIGNED ? null : field?.esIndex ? await es.client.get({id: drag?.id, index: field.esIndex, _source: ['exId', 'name']}) : dropData
          break
        case 'TagList':
          dropData = dropData === UNASSIGNED ? dropData : {id: dropData, name: drag?.label}
          break
      }
      return await assignAttributeToCatalogItemNew({assignTo: field as Partial<DetailsViewFieldConfig>, where, updatedValue: dropData, query: JSON.stringify(productQueryVariables?.query), queryItemIds: []})
    } catch (error) {

    } finally {
      await Promise.all([
        catalog.refetch('catalogItems')
      ])
    }
  }

  @action private _assignLibraryItemToCatalogItem_bulk = async (e: VIPDropEventArgs) => {
    DEBUG_CATALOG_MUTATIONS && console.log(`_assignLibraryItemToCatalogItem_bulk`, e)

    const {drag, drop, context} = e,
      {catalog, catalog: {offlineItemsByIndex, fieldByName, parseIdFromPath, selection: {selectedIds}, filter: {refetchFilterByFields}}, es, sidebar} = this,
      field = fieldByName(context.field),
      key = drag.dragType,
      targetExtraStore = drop.targetExtra?.store,
      ids = ((selectedIds.has(drop.id) ? Array.from(selectedIds.keys()) :
              drop.target == 'GroupHeader' ? targetExtraStore?.storeNode?.productStore?.catalogItemIdsArray :
              targetExtraStore?.detailsStore?.data?.id ? [targetExtraStore?.detailsStore?.data.id] : [drop.id]) ?? []).map(id => parseIdFromPath(id)),
      offlineIds = ids.filter((id) => id.includes(OFFLINE_ID)),
      onlineIds: Array<string> = _.difference(ids, offlineIds),
      query = drop.target == 'GroupHeader' ? targetExtraStore?.storeNode?.productStore?.dropHeaderUndefinedQuery : `{"terms": {"exId": ${JSON.stringify(onlineIds)} }}`,
      libraryItemId = catalog.idFor(drag?.targetExtra?.data, false) ?? drag.id,
      {where} = drag,
      isUnassign = libraryItemId === UNASSIGNED,
      tagListType = field.def?.type === 'TagList',
      listType = field.def?.type === 'EnumList' || field.def?.type === 'RefList',
      _source = tagListType || listType || context.catalogFields ? ['_id', 'exId', ...context.catalogFields ?? [context.field]] : ['_id', 'exId']

    let r

    // Update any offline (unpublished) items
    const offlineResults = await Promise.all(
      offlineIds.map(async (catalogId) => {
        const tagListType = field.def?.type === 'TagList',
          dropData = isUnassign ? {} : await es.client.get({id: libraryItemId, index: key}),
          d = tagListType === true && offlineItemsByIndex[catalog.index][catalogId],
          libraryData = d ? isUnassign ? []
                                       : [...(_.get(d, context.field) ?? []), dropData]
                          : dropData
        return this.assignAttributeToOfflineCatalogItem({assignTo: context, where, updatedValue: libraryData, offlineId: catalogId, isMultiple: true})
      })
    )

    // Update any items that exist in ES (online items)
    if (onlineIds.length > 0) {
      if (field.def?.type === 'TagList') {
        const dropData = isUnassign ? drag.id : {id: drag?.id, name: drag?.label}
        r = await this.assignAttributeToCatalogItemNew({assignTo: field as Partial<DetailsViewFieldConfig>, where, updatedValue: dropData, query: `{"terms": {"exId": ${JSON.stringify(onlineIds)} }}`})

        // TODO: Need a way to tell backend to append the newly assigned tag
        // onlineIds.map(async (catalogId:string) => {
        //   const dropData = isUnassign ? {} : await stores.es.client.get({ id: libraryItemId, index: key }),
        //     d = await stores.es.client.get({ id: catalogId, index: drop.dragType ?? catalog.index, _source: [context.field] }),
        //     libraryData = d ? isUnassign ? []
        //       : [...(_.get(d, context.field) ?? []), dropData]
        //       : dropData
        //   console.log({libraryData})
        //   await assignAttributeToCatalogItemNew({ assignTo: context.field, where, updatedValue: libraryData, query: `{"match": {"exId": "${catalogId}"}}` })
        // })
      } else {
        //console.log({fieldDef, catalogId, libraryData, mutations})
        //console.log({catalogId, toIndex, mutations})
        /* {exId: libraryItemId}, where, field: context.field, query, queryItemIds: onlineIds */
        const dropData = await es.client.get({id: libraryItemId, index: field.esIndex})
        r = await this.assignLibraryItemToCatalogItem_ByQuery({libraryItemData: dropData, where, field: context.field, query, queryItemIds: onlineIds, candidateOnly: sidebar?.left?.assignToCandidateOnly})
      }
    }
    await Promise.all([
      catalog.refetch('catalogItems'),
      refetchFilterByFields([context?.field])
    ])
    catalog.updateTableViewData({ids: onlineIds, index: field.esIndex})

    return r
  }

  @action assignLibraryItemToCatalogItem = async (drag: IDragDropElement, drop: IDragDropElement, context, dataTransfer: DataTransfer) => {
    // console.log(`Assign`, e) //drag: libraryItem // drop:catalogItem
    const e = {drag, drop, context, dataTransfer}
    return await (ui.settings.usingBulkAssignAPI || true ? this._assignLibraryItemToCatalogItem_bulk(e)
                                                         : this._assignLibraryItemToCatalogItem_old(e))
  }

  @action moveCatalogItemBetweenGroups = async (drag: IDragDropElement, drop: IDragDropElement, context, dataTransfer: DataTransfer) => {
    //drag: IDragDropElement, drop: IDragDropElement, context, dataTransfer
    // const {drag, drop, context} = e
    DEBUG_CATALOG_MUTATIONS && console.log(`moveCatalogItemBetweenGroups`, {drag: toJS(drag), drop: toJS(drop), context})
    const {es, catalog, catalog: {groupBy, parseIdFromPath, isFieldListType}} = this,
      targetGroupStore = drop.targetExtra.store as GroupByGroupStore

    let dropObj = tryParseJson(drop.id) ?? parseIdFromPath(drop.id)

    // TODO Remove any keys in the drop object that need not have changed
    // example being group 1 -> sg1, sg2 - now drag between the subgroups, no need to change the root

    // Convert values to realized values (some may be groups that we've dropped on and we need to lookup the full real object)
    let realizedDrop: any = {}
    await Promise.all(
      _.keys(dropObj).map(
        async k => {
          const field = catalog._fieldByName[k]

          if (field?.esIndex) {
            const correctStore = targetGroupStore.findMatchingStoreInHierarchyForField(field)

            if (correctStore) {
              const specId = _.first(correctStore.props.info.aggs?.ids?.buckets)?.key
              const specObject = !specId ? null : await es.get({
                index: field.esIndex,
                id: specId
              })

              _.set(realizedDrop, k, !specId ? null : (specObject ?? _.get(targetGroupStore.grouping.groupOfflineItems?.[_.get(dropObj, k)]?.[0], k)))
            } else {
              DEBUG_CATALOG_MUTATIONS && console.debug(`Should not be here - missing GroupByGroupStore on .targetExtra.store`)
              // const group = groupBy.hierarchyGroups.find(g => g.fieldName == k)
              // if (!group) {  // Drop obj can include subtitles which won't be part of the hierarchy
              //   throw new Error(`Cannot find group ${k} in hierarchy`)
              // }

              // if (group?.esIndex) {
              //   const node = group.nodes.get(stringify({[k]: dropObj[k]}))
              //   if (!node) {
              //     throw new Error(`Unable to find matching group node for ${k} - ${dropObj[k]}`)
              //   }
              //
              //   const groupIds = node.definition.aggs.ids?.buckets?.map(
              //     ({key}) => key),
              //     specObject = groupIds && await es.get({
              //       index: group.esIndex,
              //       id: _.first(groupIds)
              //     }),
              //     // Handling for groups having only Pending Items
              //     onlineOfflineItem = group.groupOfflineItems?.[dropObj[k]]?.[0]?.[k]
              //   realizedDrop[k] = specObject ?? onlineOfflineItem
              // }
            }
          } else {
            let targetGroupValue = _.get(dropObj, k)
            if (isFieldListType(field?.def?.type)) {
              let dragGroup = _.get(drag.targetExtra.data, k)
              dragGroup = new Set((_.isArray(dragGroup) ? dragGroup : [dragGroup]).filter(dg => !drag.id.includes(dg)).filter(g => _.identity(g)))
              dragGroup.add(targetGroupValue)
              targetGroupValue = Array.from(dragGroup.values())
            }
            // console.log("setting value to>>",k,targetGroupValue)
            _.set(realizedDrop, k, targetGroupValue)
          }
        })
    )

    for (var hg of targetGroupStore.hierarchyIncludingSelf) {
      if (hg?.groupDefinition?.onAssign) {
        realizedDrop = await hg.groupDefinition.onAssign(realizedDrop, {store: targetGroupStore, stores: this.stores})
      }
    }

    if (ui.settings.usingBulkAssignAPI) {
      const offlineIds = [drag.id, ...(drag.extraIds ?? [])].map(parseIdFromPath).filter((id) => id.includes(OFFLINE_ID)).filter((id) => id.includes(OFFLINE_ID))
      const onlineIds = _.difference(_.uniq([parseIdFromPath(drag.id), ...(drag.extraIds.map(parseIdFromPath) ?? [])]), offlineIds)
      const query = `{"terms": {"exId": ${stringify(onlineIds)} }}`

      await catalog.createItem.updateObjectInRemoteByQuery({
        query, toIndex: catalog.index, changes: Object.keys(dropObj).reduce(
          (acc, k) => {
            const group = groupBy.groupingByName.get(k)
            return {
              ...acc,
              [k]: {
                field: group.esIndex ? group.fieldName : group.esField,
                type: {type: group?.def?.type},
                updatedValue: _.get(realizedDrop, k, null)
              } as IMutationObj
            }
          }, {}
        ), clearLoader: true, queryItemIds: onlineIds
      })
      offlineIds.map((id) => {
        catalog.mergeIntoOfflineItem(id, realizedDrop)
      })
    } else {
      await Promise.all([drag.id, ...(drag.extraIds ?? [])].map(parseIdFromPath).map(
        async id => {
          if (id.includes(OFFLINE_ID)) {
            catalog.mergeIntoOfflineItem(id, realizedDrop)
          } else {
            const isExId = id.startsWith('C'), data = !isExId && await es.client.get({id, index: drag.dragType, _source: ['exId', 'id']}),
              mutations = Object.keys(dropObj).reduce(
                (acc, k) => {
                  const group = groupBy.groupingByName.get(k) // May not be a grouping as we could have subtitle key here

                  return {
                    ...acc,
                    [k]: {
                      field: group?.esField ?? k,
                      updatedValue: _.get(realizedDrop, k, null)
                    } as IMutationObj
                  }
                }, {}
              )

            await catalog.createItem.updateObjectInRemote({
              toIndex: catalog.index,
              toId: id,
              isMultiple: true,
              changes: mutations
            })
            // await Promise.all(
            //   Object.keys(dropObj).map(async k => {
            //     const group = groupBy.hierarchyGroups.find(g => g.fieldName == k)
            //     if (!group) {
            //       throw new Error(`Cannot find group ${k} in hierarchy`)
            //     }
            //
            //     if (group.esIndex) {
            //       // Spec assignment
            //
            //     } else {
            //       return await assignAttributeToCatalogItem({assignTo: k, exId: isExId ? id : data?.exId ?? id, updatedValue: dropObj[k]})
            //     }
            //   }))
          }
        }))
    }
    await catalog.refetch('catalogItems')

    const {filter} = catalog
    const filterRelatedRealizedDropKeys = _.intersection(_.keys(realizedDrop), filter.configuredFilterFields)
    if (filterRelatedRealizedDropKeys.length) await filter.refetch(true, filterRelatedRealizedDropKeys)
  }

  @action assignAttributeToCatalogItemNew = async ({assignTo, section, where, updatedValue, query, queryItemIds}: { assignTo: Partial<DetailsViewFieldConfig> | string, where?: any, updatedValue: any, section?: string, query: string, queryItemIds?: Array<string> }) => {
    if (!query) {
      throw new Error('A query must be specified')
    }

    DEBUG_CATALOG_MUTATIONS && DEBUG && console.debug(`assignAttributeToCatalogItemNew`, {assignTo, section, where, updatedValue, query, queryItemIds})

    const {catalog} = this

    let fieldObj: Partial<DetailsViewFieldConfig> = assignTo as DetailsViewFieldConfig
    if (_.isString(assignTo)) {
      fieldObj = catalog._fieldByName[assignTo] as Partial<DetailsViewFieldConfig>
    }

    const {def: type} = _.isEmpty(fieldObj?.def) ? catalog._fieldByName[fieldObj.field] : fieldObj
    if (!fieldObj) {
      console.error(`Unable to find field ${fieldObj.field}`)
      return
    }

    const {field} = fieldObj,
      baseNode = fieldObj.baseNode ?? field,
      {index: toIndex, createItem: {updateObjectInRemote, updateObjectInRemoteByQuery, clearLoaderFor}} = catalog
    if (!field) throw new Error(`field property is not defined on ${stringify(fieldObj)}`)

    const mutateC8 = !fieldObj?.def?.enumValues,// !fieldObj?.mutateC8 ? false : fieldObj.mutateC8,
      changes = {[baseNode]: {field: section ?? field, where, updatedValue: updatedValue === UNASSIGNED ? null : updatedValue /*: updatedName*/, mutateC8, type, baseNode}}

    // console.log({query, toIndex, idField: field, mutations, docMapping, clearLoader: false})
    await updateObjectInRemoteByQuery({
      query,
      toIndex,
      changes,
      clearLoader: false,
      queryItemIds
    })
    //clearLoaderFor exIds involved inside group
    if (!_.isEmpty(queryItemIds)) {
      await Promise.all(queryItemIds.map((exId) => {
          new Promise<void>(res => setTimeout(() => {
            clearLoaderFor(exId)
            res()
          }, 2000))
        })
      )
    }
    if (!catalog.isDataTableView) catalog.refetch('catalogItems')
  }

  @action assignAttributeToCatalogItem = async ({assignTo, section, where, updatedValue, exId, isMultiple = false}: { isMultiple?: boolean, assignTo: ICatalogFieldConfig | string, where?: any, updatedValue: any, section?: string, exId: string }) => {
    //console.trace(`assignAttributeToCatalogItem`, {assignTo, section, where, updatedValue, exId, isMultiple})

    // return this.assignAttributeToCatalogItemNew({assignTo, query:exId, updatedValue})
    if (!exId) {
      throw new Error('A centric ID must be specified')
    }

    const {catalog} = this
    let fieldObj: ICatalogFieldConfig = assignTo as ICatalogFieldConfig
    if (_.isString(assignTo)) {
      fieldObj = catalog._fieldByName[assignTo]
    }

    const {def: type} = _.isEmpty(fieldObj?.def) ? catalog._fieldByName[fieldObj.field] : fieldObj
    if (!fieldObj) {
      console.error(`Unable to find field ${fieldObj.field}`)
      return
    }

    const {field} = fieldObj,
      baseNode = fieldObj?.editor?.baseNode ?? fieldObj.section ?? field,
      {index: toIndex, createItem: {updateObjectInRemote, clearLoaderFor}} = catalog
    if (!field) throw new Error(`field property is not defined on ${stringify(fieldObj)}`)

    const mutateC8 = true,// !fieldObj?.mutateC8 ? false : fieldObj.mutateC8,
      mutations = {[baseNode]: {field: section ?? field, where, updatedValue: updatedValue === UNASSIGNED ? null : updatedValue /*: updatedName*/, mutateC8, type, baseNode}},
      {docMapping} = catalog.detailViewFor(toIndex)

    // await es.mutate({toId: exId, toIndex, idField: field, mutations, docMapping})
    await updateObjectInRemote({toId: exId, toIndex, idField: field, changes: mutations, docMapping, clearLoader: false, isMultiple})

    await new Promise<void>(res => setTimeout(() => {
      isMultiple === false && catalog.refetch('catalogItems')
      clearLoaderFor(exId)
      res()
    }, 2000))
  }

  @action assignLibraryItemToOfflineCatalogItem = async ({catalogId, dropData, key}: { libraryItemId: string, catalogId: string, dropData: any, key: string }) => {
    const {catalog: {_fieldByName, offlineItemsByIndex, index}} = this,
      fieldDef = _fieldByName[key],
      data = offlineItemsByIndex[index][catalogId]
    if (fieldDef.updateOnAssignment && _.isArray(fieldDef.updateOnAssignment)) {
      fieldDef.updateOnAssignment?.forEach(f => {
        let drop = [dropData]
        if (data[f]) {
          drop = [...data[f], {id: dropData.id, exId: dropData.exId, images: dropData.images}]
        }
        _.merge(data, {[f]: drop})
      })
    }
    // data[key] = dropData
    // _.merge(data, {[key]: dropData})
    _.set(data, key, dropData)
  }

  @action assignCatalogFieldToCatalogItems = async (drag: IDragDropElement, drop: IDragDropElement, context) => {
    // const {drag, drop, context} = e
    const {es, catalog} = this,
      {selection: {selectedIds}, sectionById, offlineItemsByIndex, index, parseIdFromPath} = catalog,
      field = catalog._fieldByName[drag.dragType],
      ids = (selectedIds.has(drop.id) ? Array.from(selectedIds.keys()) : [drop.id]).map(id => parseIdFromPath(id)),
      {where} = drag,
      isMultiple = ids.length > 1

    // console.log(`assignCatalogFieldToCatalogItems`, toJS({drag: _.omit(drag, 'targetExtra'), drop: _.omit(drop, 'targetExtra'), isMultiple, field, where, ids}, {recurseEverything: true})) //drag: libraryItem // drop:catalogItem
    if (ui.settings.usingBulkAssignAPI || true) {
      //separate out Pending Items from selectedIds
      const offlineIds = ids.filter((id) => id.includes(OFFLINE_ID))
      let dropData: any = drag?.id ?? drag['id']
      switch (field?.def?.type) {
        case 'Ref':
        case 'RefList':
          dropData = dropData === UNASSIGNED ? null : field?.esIndex ? await es.client.get({id: drag?.id, index: field.esIndex, _source: ['exId', 'name']}) : dropData
          break
        case 'TagList':
          dropData = dropData === UNASSIGNED ? dropData : {id: dropData, name: drag?.label}
          break
      }
      await Promise.all([...(offlineIds || [])].map((id: any) => {
        //TODO: Pending Item mutation to be handled with query
        this.assignAttributeToOfflineCatalogItem({assignTo: field, updatedValue: dropData, offlineId: id})
      }))
      const onlineIds = _.difference(ids, offlineIds)
      if (!_.isEmpty(onlineIds)) {
        // we don't need to fetch any kind of data from ES before doing mutations
        await this.assignAttributeToCatalogItemNew({assignTo: field as Partial<DetailsViewFieldConfig>, where, updatedValue: dropData, query: `{"terms": {"exId": ${stringify(onlineIds)} }}`})
      }
      catalog.updateTableViewData({ids, index: field?.esIndex ?? catalog.index})
    }
  }

  @action assignAttributeToOfflineCatalogItem = async ({assignTo: {field, section, updateOnAssignment, def}, where, updatedValue, offlineId, isMultiple = false}: { assignTo: ICatalogFieldConfig, where?: any, updatedValue: any, offlineId: string, isMultiple?: boolean }) => {
    const {catalog, catalog: {sectionConfigFor, fieldByName, offlineItemsByIndex, index, itemDetailStores}} = this
    const data = offlineItemsByIndex[index][offlineId],
      catalogItemDetailsStore = _.last(Array.from(itemDetailStores.values()).filter((obj) => obj.id === offlineId)),
      sectionConfig = catalog.sectionConfigFor(section),
      isUnassign = updatedValue.id === UNASSIGNED

    const fieldWithDef = fieldByName(field)

    if (!_.isEmpty(sectionConfig)) {
      const sectionValues = _.get(data, section),
        d = sectionValues.map(s => {
          if (_.get(s, sectionConfig.viewOptions.field) === where?.[`${section}.${sectionConfig.viewOptions.field}`]) {
            _.set(s, field.replace(`${section}.`, ''), updatedValue)
          }
          return s
        })
      if (updateOnAssignment && !_.isArray(updateOnAssignment)) {
        const {expression: {field: f, computeField: fPath, assignTo: assignToFn}} = updateOnAssignment
        _.set(data, f, assignToFn(Array.isArray(d) ? d.length === 0 ? null : d?.map(v => _.isNumber(_.get(v, fPath)) ? _.toNumber(_.get(v, fPath) ?? 0) : _.get(v, fPath)) : 0))
      }
      _.set(data, section, d)

      catalogItemDetailsStore?.originalData && (catalogItemDetailsStore.replaceData(data))
    } else {
      const {originalData} = (catalogItemDetailsStore ?? {})
      const existingValue = _.get(originalData, field)
      const fieldType = def?.type ?? fieldWithDef?.def?.type
      if (_.isArray(existingValue) ||
          _.includes(['EnumList', 'RefList', 'TagList'] as Array<CustomAttributeType>, fieldType)) {
        const newArrValue = wrapScalarOrArrayAsOneDArray(isUnassign ? null : updatedValue)
        const existingArrVal = wrapScalarOrArrayAsOneDArray(existingValue)
        let updatedVal = isUnassign || _.isEmpty(newArrValue) ? [] : _.unionBy(newArrValue, existingArrVal, 'id')
        if (fieldType === 'RefList') {
          updatedVal = _.unionBy(newArrValue, existingArrVal, 'id')
        }
        _.set(data, field, updatedVal)
        originalData && _.set(originalData, field, updatedVal)
      } else {
        originalData && _.set(originalData, field, updatedValue)
        _.set(data, field, updatedValue)
      }
    }

    await catalog.upsertOfflineItem({index: index || catalog.index, value: data})
  }

  @action assignImageToCatalogItem = async (drag: IDragDropElement, drop: IDragDropElement, context, dt: DataTransfer) => {
    DEBUG_CATALOG_MUTATIONS && DEBUG && console.debug(`assignImageToCatalogItem`, {drag, drop, context, dt})

    const {catalog, catalog: {sidebar, img: {getImageFromDragImage, readImageFromDataTransfer}, isDataTableView, selection: {selectedIds}, parseIdFromPath, img: {addImagesToOfflineItem, uploadImagesToC8, prepareUploads}}, site: {onError}} = this,
      ids = (selectedIds.has(drop.id) ? Array.from(selectedIds.keys()) : [drop.id]).map(parseIdFromPath)

    let result
    try {
      const f: FileList | File = await getImageFromDragImage(dt)

      const fileImages = await readImageFromDataTransfer(dt, f),
        files = f instanceof File ? {file: f} as IHasFile : f

      if (drop?.targetExtra?.detailsStore) {
        return sidebar?.right?.type === 'sidebar'
               ? drop?.targetExtra?.detailsStore.onAddImages(fileImages)
               : drop?.targetExtra?.detailsStore?.modal?.details?.onAddImages(fileImages)
      }

      if (ids.length > 0) {
        const offlineSelectedProductIds: Array<string> = ids.filter((id) => id.includes(OFFLINE_ID))
        if (offlineSelectedProductIds.length > 0) {
          const uploadedImages = await uploadImagesToC8(fileImages),
            result = await Promise.all(
              offlineSelectedProductIds.map(async id => {
                addImagesToOfflineItem(id, uploadedImages)
              })
            )
        }

        const onlineSelectedProductIds: Array<string> = ids.filter((id) => !id.includes(OFFLINE_ID))
        if (onlineSelectedProductIds.length > 0) {
          //console.log(`Assign ${files && files.length ? files.length : 1} ${drag.dragType} to`, drop.id)
          if (files) {
            let uploadInput: IUpload = {
              entityIds: onlineSelectedProductIds,
              files: files,
              isRefetchNeeded: true,
              targetIndex: drop?.target
            }
            result = await prepareUploads(uploadInput)
          } else {
            onError(`Seems like files has restriction to upload Or Not a valid upload`)
          }
        }
      }
      if (isDataTableView) {
        catalog.updateTableViewData({ids, index: catalog.index})
        ids.map(id => {
          const {flexLayout: {tabNodes}} = this

          StoresInstance.allStores.forEach(stores => {
            if (stores?.sidebar.right?.details?.id === id) {
              stores?.sidebar.right?.details.loadData()
            }
          })
        })
      }
      return result
    } catch (e) {
      onError(`Error while reading file from drag item ${e}`)
    }
  }

  /// region Deprecated
  /**
   * Looks up the individual full records for the catalog item(s) being dropped on, modifies a value depending on the drag record, then upserts the complete record.  Very expensive.
   * @param e
   */
  @action private _assignLibraryItemToCatalogItem_old = async (e: VIPDropEventArgs) => {
    DEBUG_CATALOG_MUTATIONS && console.log(`_assignLibraryItemToCatalogItem_old`, e)

    const {drag, drop, context} = e,
      {es, catalog, catalog: {index, offlineItemsMap, filter: {refetchFilterByFields}, sidebar: {left: {assignToCandidateOnly}}}} = this,
      key = drag.dragType,
      field = this.catalog.fieldByName(context.field),
      tagListType = field.def?.type === 'TagList',
      enumListType = field.def?.type === 'EnumList',
      _source = tagListType || context.catalogFields ? ['_id', 'exId', ...context.catalogFields ?? [context.field]] : ['_id', 'exId']

    let items = [], offlineItems = []
    switch (drop.target) {
      case 'GroupHeader': {
        DEBUG_CATALOG_MUTATIONS && console.debug(`Looking up full data record for group`, drop?.targetExtra?.store.groupTitleLabel)

        const {childGroupStore: {productQueryVariables}, offlineItems: oI, titleCount: size} = drop?.targetExtra?.store,
          {items: i} = es.unwrapHitsResult(await es.search({
            index,
            meta: `Get field data for groupBy aggregate sidebar - group '${drop?.targetExtra?.store.groupTitleLabel}'`,
            body: {
              size,
              ...productQueryVariables,
              _source
            }
          }))
        items = i
        offlineItems = oI

        break
      }

      default:
        // Drop on catalog items

        const {catalog: {parseIdFromPath, selection: {selectedIds}}} = this,
          values = (selectedIds.has(drop.id) ? Array.from(selectedIds.keys()) : [drop.id]).map(id => parseIdFromPath(id)),
          oI = drop.id.startsWith(OFFLINE_ID) ? [...values.map(val => offlineItemsMap[val]?.item?.data ?? drop)] : [],
          filteredValues = values.filter(v => !v.startsWith(OFFLINE_ID))
        const {items: i} = filteredValues.length ? es.unwrapHitsResult(await es.search({
          index,
          meta: 'Get Products details',
          body: {
            size: filteredValues.length,
            query: {bool: {filter: {ids: {values: filteredValues}}}},
            _source
          }
        })) : {items: []}
        items = i
        offlineItems = oI

        break
    }

    const {assignAttributeToOfflineCatalogItem} = this
    const r = await Promise.all(
      [...items, ...offlineItems].map(async (item) => {
        const libraryItemId = drag.id,
          {where} = drag,
          catalogItemId = item?.exId ?? item?._id ?? item.id,
          isOffline = catalogItemId.includes(OFFLINE_ID),
          isUnassign = libraryItemId === UNASSIGNED,
          dropData = isUnassign ? {} : await es.client.get({id: libraryItemId, index: key}),
          //d = catalogId, //tagListType === true && (isOffline ? catalog.offlineItemsByIndex[catalog.index][catalogId] : await stores.es.client.get({id: catalogId, index: drop.dragType ?? catalog.index, _source: [context.field]})),
          libraryData = item && (tagListType || enumListType) ? isUnassign ? [] : [...(_.get(item, context.field) ?? []), dropData] : dropData
        // const dropData = await stores.es.client.get({id: drag.id, index: key})

        // If the same library item is already assigned, no action is needed
        // YFTS-6478
        if (item && _.find((_.get(item, context.field) ?? []), {id: dropData.id})) {
          return true
        }
        if (isOffline) {
          // await assignLibraryItemToOfflineCatalogItem({catalogId, dropData: libraryData, libraryItemId, key: context.field})
          await assignAttributeToOfflineCatalogItem({assignTo: context, where, updatedValue: libraryData, offlineId: catalogItemId, isMultiple: true})
        } else {
          const {assignAttributeToCatalogItem, assignLibraryItemToCatalogItem_old} = this
          return tagListType ? await assignAttributeToCatalogItem({assignTo: context.field, where, updatedValue: libraryData, exId: item.exId, isMultiple: true})
                             : await assignLibraryItemToCatalogItem_old({catalogId: catalogItemId, libraryData, where, field: context.field, isMultiple: true, data: item, candidateOnly: drag.target === drop.target ? false : assignToCandidateOnly})
          //console.log(`assignLibraryItemToCatalogItem`, drag, drop, catalogId, libraryItemId, context)
        }
      }))

    await catalog.refetch('catalogItems')
    await refetchFilterByFields([context?.field])

    return r
  }

  /// endregion

  /***
   * Assigns a library item (located in a remote index) to one or more catalog items
   * @param field
   * @param where
   * @param libraryItemData
   * @param query
   * @param queryItemIds
   * @param force
   */
  @action assignLibraryItemToCatalogItem_ByQuery = async ({field, where, libraryItemData, query, queryItemIds, candidateOnly}: { field: string, where?: any, libraryItemData: any, query: string, queryItemIds?: Array<string>, candidateOnly: boolean }, force = false) => {
    DEBUG_CATALOG_MUTATIONS && console.log(`assignLibraryItemToCatalogItem_ByQuery`, {field, where, libraryItemData, query, queryItemIds})

    const {catalog: {index: toIndex, createItem: {updateObjectInRemoteByQuery}, refetch}} = this,
      mutations = {
        [field]: {
          field,
          updatedValue: libraryItemData,
          where
        }
      }
    //console.log({catalogId, toIndex, mutations})
    const r = await updateObjectInRemoteByQuery({query, toIndex, changes: mutations, clearLoader: true, queryItemIds, candidateOnly})

    await refetch('catalogItems')

    return r
  }

  /**
   * Expensive, requires the entire object to have been pulled client-side
   * Replaced by bulk api (update by query)
   * @param field
   * @param where
   * @param catalogId
   * @param libraryData
   * @param isMultiple
   * @param data
   * @param candidateOnly
   * @param force
   */
  @action assignLibraryItemToCatalogItem_old = async ({field, where, catalogId, libraryData, isMultiple = false, data, candidateOnly}: { field: string, where?: any, catalogId: string, libraryData: any, isMultiple?: boolean, data?: any, candidateOnly?: boolean }, force = false) => {
    DEBUG_CATALOG_MUTATIONS && console.log(`assignLibraryItemToCatalogItem_old`, {field, where, catalogId, libraryData, isMultiple, data, candidateOnly})

    if (libraryData) {
      const {catalog: {index: toIndex, createItem: {updateObjectInRemote}, refetch}} = this,
        mutations = {[field]: {field, updatedValue: libraryData, where}}
      //console.log({fieldDef, catalogId, libraryData, mutations})

      //console.log({catalogId, toIndex, mutations})
      const r = await updateObjectInRemote({
        toId: catalogId,
        toIndex,
        changes: mutations,
        isMultiple,
        data,
        candidateOnly
      })

      if (!isMultiple && r !== false && !(_.isArray(r) && r.some(v => v === false))) {
        await refetch('catalogItems')
      }

      return r
    } else {
      throw new Error(`assignLibraryItemToCatalogItem_old - unhandled case ${libraryData}`)
    }
    /*const {
        catalog: {
          refetch, index: toIndex, workspaceConfig: {_id},
          catalogConfig: {dragType, dragType: {entityType, field: dragField, index: dragIndex, path, source}}
        }
      } = this
    await es.assignToField({
      pathFromSource: path,
      entityType: entityType,
      unassign: false,
      from: {
        filter: {
          query: {
            match: {
              _id: libraryItemId
            }
          },
          _source: source
        }, index: dragIndex
      },
      to: {
        field: dragField,
        filter: {
          terms: {
            _id: [catalogId]
          }
        }
        , index: toIndex
      },
      workspaceId: _id,
      isUpdated: true
    })*/

    // await refetch()
  }

  @action toggleBooleanField = async ({field, index, data, itemId, value, noRefetch}: { value?: boolean, field: string, index: string, data: any, itemId?: string, noRefetch?: boolean }) => {
    const {catalog, catalog: {createItem: {updateObjectInRemote}}} = this,
      v = _.isBoolean(value) ? value : _.get(data, field) === true,
      isOffline = catalog.isOfflineItem(data)

    DEBUG_CATALOG_MUTATIONS && console.log({v, field, index, data, itemId, isOffline})

    if (isOffline) {
      await catalog.upsertOfflineItem({index: catalog.index, value: _.set(data, field, !v)})
    } else {
      // this.data['attributes.Active'] = !!active
      _.set(data, field, !v)
      await updateObjectInRemote({
        toIndex: index,
        toId: itemId ?? catalog.idFor(data),
        changes: {
          [field]: {
            field: field,
            updatedValue: !v,
            type: {type: 'Boolean'},
            mutateC8: true
          }
        }
      })
      !noRefetch && catalog.refetch('catalogItems')
    }
  }
}


// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogMutationsStore.tsx